import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackScrollDepth, trackTimeOnPage, trackExitIntent, trackOutboundLink, useAnalyticsConsent } from '../utils/analytics';

/**
 * Hook to automatically track engagement metrics on the current page.
 * Attaches scroll, time-on-page, and exit-intent trackers on mount
 * and cleans them up when the page changes or component unmounts.
 *
 * @param {string} [customPageName] - Override for the page name in reports.
 *   Defaults to the current pathname.
 */
const useEngagementTracking = (customPageName) => {
    const location = useLocation();
    const pageName = customPageName || location.pathname;
    const consent = useAnalyticsConsent();

    useEffect(() => {
        if (!consent) return;

        const cleanupScroll = trackScrollDepth(pageName);
        const cleanupTime = trackTimeOnPage(pageName);
        const cleanupExit = trackExitIntent(pageName);

        const handleGlobalClick = (e) => {
            const link = e.target.closest('a');
            if (link && link.href && link.hostname && link.hostname !== window.location.hostname) {
                trackOutboundLink(link.href);
            }
        };

        document.addEventListener('click', handleGlobalClick);

        return () => {
            cleanupScroll();
            cleanupTime();
            cleanupExit();
            document.removeEventListener('click', handleGlobalClick);
        };
    }, [pageName, consent]);
};

export default useEngagementTracking;
